// What a re-import writes, from the Apply's dry run: the games it adds, the
// ones it changes and the ones it removes. A game to remove that has referees
// is confirmed one by one; until each is, Apply refuses the whole import

import { useMemo } from 'react';
import moment from 'moment';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import { time24To12H } from '../utils/misc';
import { GameTypes } from '../utils/enums';
import './ScheduleImportDiff.scss';

// An importKey is `date|field name|start`
const parseKey = (key) => {
  const parts = key.split('|');
  return {
    date: parts[0],
    field: parts.slice(1, -1).join('|'),
    start: parts.at(-1),
  };
};

const typeLabel = (type) => (type === GameTypes.PLAYOFF ? 'Playoff' : 'Pool');

const byTime = (a, b) =>
  a.date.localeCompare(b.date) ||
  a.start.localeCompare(b.start) ||
  a.field.localeCompare(b.field);

export default function ScheduleImportDiff({
  tournament,
  draft,
  dryRun,
  isChecking,
  confirmed,
  onConfirm,
}) {
  const { refereesById } = useFirebase();
  const { openModal } = useModal();

  const groups = useMemo(() => {
    if (!dryRun) return [];
    const stored = new Map((tournament.games ?? []).map((g) => [g.id, g]));
    const storedFields = new Map(
      (tournament.fields ?? []).map((f) => [f.id, f.name]),
    );
    const draftFields = new Map(draft.fields.map((f) => [f.key, f.name]));
    const next = new Map(draft.games.map((g) => [g.key, g]));

    const changeText = (name, before, after) => {
      if (name === 'division')
        return `Division: ${before?.division || '—'} → ${after.division}`;
      if (name === 'duration')
        return `Length: ${before?.duration ?? '—'} → ${after.duration} min`;
      if (name === 'field')
        return (
          `Field: ${storedFields.get(before?.field) ?? '—'} → ` +
          (draftFields.get(after.field) ?? after.field)
        );
      if (name === 'type')
        return `Type: ${typeLabel(before?.type)} → ${typeLabel(after.type)}`;
      return name;
    };

    const added = dryRun.games.created.map((key) => ({
      id: key,
      ...parseKey(key),
      division: next.get(key)?.division,
    }));
    const changed = dryRun.games.updated.map(({ gameId, key, changes }) => {
      const before = stored.get(gameId);
      const after = next.get(key);
      return {
        id: gameId,
        ...parseKey(key),
        division: after?.division,
        detail: after
          ? changes.map((name) => changeText(name, before, after)).join('; ')
          : changes.join(', '),
      };
    });
    const removed = [
      ...dryRun.games.refused.map((g) => ({ ...g, hasReferees: true })),
      ...dryRun.games.deleted,
    ].map(({ gameId, key, referees, hasReferees }) => ({
      id: gameId,
      ...parseKey(key),
      division: stored.get(gameId)?.division,
      referees: referees.map((uid) => refereesById.get(uid)?.name || 'Unknown'),
      hasReferees: !!hasReferees,
    }));

    return [
      { kind: 'added', title: 'Added', detail: '', rows: added },
      { kind: 'changed', title: 'Changed', detail: 'Change', rows: changed },
      { kind: 'removed', title: 'Removed', detail: 'Referees', rows: removed },
    ].map((group) => ({ ...group, rows: group.rows.toSorted(byTime) }));
  }, [dryRun, draft, tournament.games, tournament.fields, refereesById]);

  if (!dryRun)
    return (
      <div className='schedule-import-diff'>
        <h5>Changes</h5>
        <p className='gray-600'>
          {isChecking
            ? 'Working out what the import changes…'
            : 'The changes show once the schedule passes its checks'}
        </p>
      </div>
    );

  const days = new Set(groups.flatMap((g) => g.rows.map((row) => row.date)));
  const when = (row) =>
    (days.size > 1 ? moment(row.date).format('ddd M/D') + ', ' : '') +
    time24To12H(row.start);

  const [added, changed, removed] = groups.map((g) => g.rows.length);
  const dropped = groups[2].rows
    .filter((row) => row.hasReferees && confirmed.has(row.id))
    .reduce((sum, row) => sum + row.referees.length, 0);

  const confirmRemoval = (row) =>
    openModal({
      header: 'Remove game',
      body: (
        <p className='font-16'>
          The{' '}
          <span className='font-medium'>{`${when(row)}, ${row.field}`}</span>{' '}
          game is not on the new tab. Remove it and take{' '}
          <span className='font-medium'>{row.referees.join(', ')}</span> off it?
        </p>
      ),
      buttons: [
        { text: 'Cancel', className: 'bw' },
        {
          text: 'Remove',
          className: 'danger',
          onClick: () => onConfirm(row.id, true),
        },
      ],
    });

  return (
    <div className='schedule-import-diff'>
      <h5>
        Changes{isChecking && <span className='gray-600'> · checking…</span>}
      </h5>
      <div className='summary'>
        <p className='font-medium'>
          {added + changed + removed === 0
            ? 'No game changes: Apply only records this file as the source'
            : `${added} added · ${changed} changed · ${removed} removed`}
        </p>
        {dryRun.keptReferees > 0 && (
          <p>
            Referees stay on the matched games: {dryRun.keptReferees} assignment
            {dryRun.keptReferees === 1 ? '' : 's'}.
          </p>
        )}
        {dropped > 0 && (
          <p>
            The removed games take {dropped} assignment
            {dropped === 1 ? '' : 's'} with them.
          </p>
        )}
        {dryRun.fields.length > 0 && (
          <p>New fields: {dryRun.fields.join(', ')}</p>
        )}
      </div>
      {groups
        .filter((group) => group.rows.length > 0)
        .map((group) => (
          <div key={group.kind} className={`group ${group.kind}`}>
            <h6>{`${group.title} (${group.rows.length})`}</h6>
            <ul className='tab-header'>
              <li className='col-1'>Time</li>
              <li className='col-2'>Field</li>
              <li className='col-12'>Time, Field</li>
              <li className='col-3'>Division</li>
              <li className='col-4'>{group.detail}</li>
              {group.kind === 'removed' && <li className='col-5' />}
            </ul>
            {group.rows.map((row) => {
              const isConfirmed = confirmed.has(row.id);
              return (
                <div
                  key={row.id}
                  className={
                    'diff-row' +
                    (row.hasReferees ? ' has-referees' : '') +
                    (isConfirmed ? ' confirmed' : '')
                  }
                >
                  <p className='col-1'>{when(row)}</p>
                  <p className='col-2'>{row.field}</p>
                  <div className='col-12'>
                    <p>{when(row)}</p>
                    <p>{row.field}</p>
                  </div>
                  <p className='col-3'>{row.division || '—'}</p>
                  <p className='col-4'>
                    {group.kind === 'removed'
                      ? row.referees.join(', ') || '—'
                      : row.detail}
                  </p>
                  {group.kind === 'removed' && (
                    <div className='col-5'>
                      {row.hasReferees &&
                        (isConfirmed ? (
                          <button
                            type='button'
                            className='small bw'
                            onClick={() => onConfirm(row.id, false)}
                          >
                            Undo
                          </button>
                        ) : (
                          <button
                            type='button'
                            className='small danger'
                            onClick={() => confirmRemoval(row)}
                          >
                            Remove
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
    </div>
  );
}
